import type { ReportChartValue } from '@reports/domain/types';

/**
 * Value object representing a chart data visualization
 *
 * Configuration defines HOW to visualize data as a chart.
 * The actual data comes from the API in tabular format (columns + rows).
 *
 * @example
 * const chartBlockData: ChartBlockData = {
 *   key: 'sales',
 *   series: ['orders', 'sales'],
 *   values: [{ x: '2025-01-01', y: [100, 200] }, { x: '2025-01-02', y: [300, 400] }],
 * };
 */
export type ChartBlockData = Readonly<{
  key: string;
  series: ReadonlyArray<string>;
  values: ReadonlyArray<ReportChartValue>;
}>;

type CreateChartBlockDataProps = {
  key: string;
  series: string[];
  values: { x: string; y: number[] }[];
};

/**
 * Factory function to create a chart block data
 *
 * @param props - The properties to create the chart block data
 * @returns {ChartBlockData} The created chart block data
 *
 * @example
 * const chartBlockData: ChartBlockData = createChartBlockData({
 *   key: 'sales',
 *   series: ['orders', 'sales'],
 *   values: [{ x: '2025-01-01', y: [100, 200] }, { x: '2025-01-02', y: [300, 400] }],
 * });
 */
export const createChartBlockData = (props: CreateChartBlockDataProps): ChartBlockData => {
  if (!props.key) {
    throw new Error('Key is required');
  }

  if (!props.series || props.series.length === 0) {
    throw new Error('Series are required');
  }

  for (const value of props.values) {
    if (!value.x || !value.y || value.y.length === 0) {
      throw new Error('Value x and y are required');
    }
  }

  return Object.freeze(props);
};
